"use client";

import {
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type FormEvent,
  type KeyboardEvent,
  type ReactNode,
} from "react";
import { useRouter } from "next/navigation";
import type {
  WSearchContent,
  WSearchAutocompleteGroup,
  WSearchAutocompleteItem,
} from "@/lib/api/cms-window-content";
import { useT, useLocale } from "@/lib/i18n/context";
import { wSearchTranslations } from "./WSearch.i18n";
import { extractInternalHref } from "@/lib/utils/extractInternalHref";
import styles from "./WSearch.module.css";

/** Opóźnienie debounce dla zapytań autocomplete (ms). */
const DEBOUNCE_MS = 250;

/**
 * WSearch - okno wyszukiwarki: IKONA lupy, a po kliknięciu MODAL z interfejsem
 * wyszukiwania (input + autocomplete + popularne wyszukiwania).
 *
 * **Data source:** `W_SEARCH_CONTROLLER::getApiContent()` -> `config`
 * (results_url, min_chars, popular_searches). Podpowiedzi: route handler
 * `/api/w-search/autocomplete` (proxy do `IAPI_autocomplete`).
 *
 * **UX:** ikona -> modal (role=dialog, aria-modal); w modalu autocomplete
 * (debounce 250ms, grupy catalogs/products/tags, klawiatura, WCAG combobox/listbox)
 * + chipy popularnych fraz. Escape: zamyka dropdown, a gdy zamknięty - modal.
 * Klik w tło / przycisk X - zamyka modal.
 */
export function WSearch({ content }: WSearchProps) {
  const t = useT(wSearchTranslations);
  const locale = useLocale();
  const router = useRouter();

  const minChars = content?.config?.min_chars ?? 2;
  const resultsUrl = content?.config?.results_url ?? "";
  const popular = Array.isArray(content?.config?.popular_searches)
    ? content.config.popular_searches
    : [];

  const [modalOpen, setModalOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [groups, setGroups] = useState<WSearchAutocompleteGroup[]>([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);

  const listboxId = useId();
  const titleId = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const abortRef = useRef<AbortController | null>(null);

  const flatItems = useMemo(() => groups.flatMap((g) => g.items), [groups]);

  /* Debounced fetch podpowiedzi (tylko gdy modal otwarty). */
  useEffect(() => {
    if (!modalOpen) return;
    const q = query.trim();
    if (q.length < minChars) {
      setGroups([]);
      setOpen(false);
      setActiveIndex(-1);
      setLoading(false);
      return;
    }

    setLoading(true);
    const handle = setTimeout(() => {
      abortRef.current?.abort();
      const ctrl = new AbortController();
      abortRef.current = ctrl;

      fetch(`/api/w-search/autocomplete?q=${encodeURIComponent(q)}&lang=${locale}`, {
        signal: ctrl.signal,
      })
        .then((res) => res.json())
        .then((data: { groups?: WSearchAutocompleteGroup[] }) => {
          setGroups(Array.isArray(data?.groups) ? data.groups : []);
          setOpen(true);
          setActiveIndex(-1);
        })
        .catch((e) => {
          if (!(e instanceof DOMException && e.name === "AbortError")) {
            setGroups([]);
          }
        })
        .finally(() => setLoading(false));
    }, DEBOUNCE_MS);

    return () => clearTimeout(handle);
  }, [query, minChars, locale, modalOpen]);

  /* Modal open: focus input + body scroll lock. Close: focus z powrotem na ikonę. */
  useEffect(() => {
    if (!modalOpen) return;
    inputRef.current?.focus();
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, [modalOpen]);

  function closeModal() {
    setModalOpen(false);
    setOpen(false);
    setActiveIndex(-1);
    /* Powrót focusa na trigger (WCAG). */
    triggerRef.current?.focus();
  }

  function goToResults(phrase: string) {
    const p = phrase.trim();
    if (resultsUrl === "" || p === "") return;
    const target = extractInternalHref(resultsUrl) ?? resultsUrl;
    const sep = target.includes("?") ? "&" : "?";
    const full = `${target}${sep}q=${encodeURIComponent(p)}`;
    setModalOpen(false);
    if (target.startsWith("/")) router.push(full);
    else window.location.href = full;
  }

  function selectItem(item: WSearchAutocompleteItem) {
    setOpen(false);
    setModalOpen(false);
    if (item.url) {
      const internal = extractInternalHref(item.url);
      if (internal !== null) router.push(internal);
      else window.location.href = item.url;
    } else {
      /* Tag (url=null) -> strona wyników z frazą = nazwa tagu. */
      goToResults(item.value);
    }
  }

  function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (activeIndex >= 0 && flatItems[activeIndex]) {
      selectItem(flatItems[activeIndex]);
    } else {
      goToResults(query);
    }
  }

  function onInputKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      if (flatItems.length === 0) return;
      setOpen(true);
      setActiveIndex((i) => (i + 1) % flatItems.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      if (flatItems.length === 0) return;
      setOpen(true);
      setActiveIndex((i) => (i <= 0 ? flatItems.length - 1 : i - 1));
    } else if (e.key === "Escape") {
      /* Escape: najpierw zamknij dropdown, dopiero potem (gdy zamknięty) modal. */
      if (open) {
        e.stopPropagation();
        setOpen(false);
        setActiveIndex(-1);
      } else {
        closeModal();
      }
    }
  }

  const showDropdown =
    open && query.trim().length >= minChars && (loading || flatItems.length > 0);
  const optionId = (idx: number) => `${listboxId}-opt-${idx}`;

  if (!content) return null;

  let runningIndex = -1;

  return (
    <div className={styles.root}>
      {/* Trigger: tylko ikona lupy */}
      <button
        type="button"
        ref={triggerRef}
        className={styles.iconButton}
        aria-label={t.open}
        aria-haspopup="dialog"
        aria-expanded={modalOpen}
        onClick={() => setModalOpen(true)}
      >
        <SearchIcon />
      </button>

      {modalOpen && (
        <div
          className={styles.overlay}
          role="presentation"
          onMouseDown={(e) => {
            /* Klik w tło (nie w panel) zamyka modal. */
            if (e.target === e.currentTarget) closeModal();
          }}
        >
          <div
            className={styles.modal}
            role="dialog"
            aria-modal="true"
            aria-labelledby={titleId}
            onKeyDown={(e) => {
              if (e.key === "Escape" && !open) closeModal();
            }}
          >
            <div className={styles.header}>
              <h2 id={titleId} className={styles.title}>
                {t.title}
              </h2>
              <button
                type="button"
                className={styles.close}
                aria-label={t.close}
                onClick={closeModal}
              >
                <CloseIcon />
              </button>
            </div>

            <form className={styles.form} role="search" aria-label={t.searchLabel} onSubmit={onSubmit}>
              <div className={styles.inputWrap}>
                <span className={styles.inputIcon} aria-hidden="true">
                  <SearchIcon />
                </span>
                <input
                  ref={inputRef}
                  type="search"
                  className={styles.input}
                  placeholder={t.placeholder}
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  onKeyDown={onInputKeyDown}
                  role="combobox"
                  aria-expanded={showDropdown}
                  aria-controls={listboxId}
                  aria-autocomplete="list"
                  aria-activedescendant={activeIndex >= 0 ? optionId(activeIndex) : undefined}
                  aria-label={t.inputLabel}
                  autoComplete="off"
                />
              </div>

              {showDropdown && (
                <div
                  className={styles.listbox}
                  id={listboxId}
                  role="listbox"
                  aria-label={t.suggestionsLabel}
                >
                  {loading && flatItems.length === 0 && (
                    <div className={styles.loading}>{t.loading}</div>
                  )}
                  {groups.map((group) => (
                    <div
                      key={group.key}
                      role="group"
                      aria-label={t.groupLabel(group.key)}
                      className={styles.group}
                    >
                      <div className={styles.groupTitle} aria-hidden="true">
                        {t.groupLabel(group.key)}
                      </div>
                      {group.items.map((item) => {
                        runningIndex += 1;
                        const idx = runningIndex;
                        const active = idx === activeIndex;
                        return (
                          <div
                            key={`${group.key}-${idx}`}
                            id={optionId(idx)}
                            role="option"
                            aria-selected={active}
                            className={`${styles.option}${active ? " " + styles.optionActive : ""}`}
                            onMouseDown={(e) => {
                              e.preventDefault();
                              selectItem(item);
                            }}
                            onMouseEnter={() => setActiveIndex(idx)}
                          >
                            {highlight(item.label, query)}
                          </div>
                        );
                      })}
                    </div>
                  ))}
                </div>
              )}
            </form>

            {/* Popularne wyszukiwania - z zapisanych fraz (c_search_keywords) */}
            {!showDropdown && popular.length > 0 && (
              <div className={styles.popular}>
                <div className={styles.popularLabel}>{t.popularLabel}</div>
                <div className={styles.chips}>
                  {popular.map((phrase) => (
                    <button
                      key={phrase}
                      type="button"
                      className={styles.chip}
                      onClick={() => goToResults(phrase)}
                    >
                      {phrase}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {content.config && !content.config.configured && (
              <p className={styles.notConfigured}>{t.notConfigured}</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export type WSearchProps = {
  content: WSearchContent;
};

/* ─── Icons (inline SVG, bez zależności) ───────────────────────────────── */

function SearchIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="11" cy="11" r="7" />
      <line x1="21" y1="21" x2="16.65" y2="16.65" />
    </svg>
  );
}

function CloseIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );
}

/* ─── Helpers ──────────────────────────────────────────────────────────── */

/** Podświetla dopasowany fragment etykiety (`<mark>`). Case-insensitive, pierwszy match. */
function highlight(label: string, query: string): ReactNode {
  const q = query.trim();
  if (q === "") return label;
  const idx = label.toLowerCase().indexOf(q.toLowerCase());
  if (idx === -1) return label;
  return (
    <>
      {label.slice(0, idx)}
      <mark className={styles.mark}>{label.slice(idx, idx + q.length)}</mark>
      {label.slice(idx + q.length)}
    </>
  );
}

